import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { getFetchableStaticUrl, getUrlForStaticApi } from "../../../../utils/urlStaticFiles";
import downloadFile from "../../../../utils/downloadFile";
import CloudDownloadIcon from "../../../../icons/CloudDownloadIcon";

const isPdfDocument = (document) => {
  const format = String(document?.format || "").toLowerCase();
  const url = String(document?.url || "").toLowerCase();
  const name = String(document?.original_name || "").toLowerCase();
  return format === "pdf" || url.includes(".pdf") || name.endsWith(".pdf") || !format;
};

const isStorageErrorBlob = async (response, blob) => {
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (!contentType.includes("xml") && !contentType.includes("text/html") && !contentType.includes("json")) {
    return false;
  }
  const text = await blob.text();
  return /AccessDenied|Access Denied|<\s*Error[\s>]/i.test(text);
};

const fetchPdfBlobUrl = async (url) => {
  const absolute = getUrlForStaticApi(url);
  const fetchable = getFetchableStaticUrl(url);
  const candidates = [fetchable, absolute].filter((item, index, list) => item && list.indexOf(item) === index);

  let lastError = "Не удалось загрузить файл";
  for (const candidate of candidates) {
    try {
      const response = await fetch(candidate, { credentials: "include" });
      if (!response.ok) {
        lastError = `Ошибка загрузки (${response.status})`;
        continue;
      }
      const blob = await response.blob();
      if (await isStorageErrorBlob(response, blob.slice(0, 2048))) {
        lastError = "Нет доступа к файлу в хранилище";
        continue;
      }
      const type = blob.type || "application/pdf";
      const pdfBlob = type.includes("pdf") ? blob : new Blob([blob], { type: "application/pdf" });
      return URL.createObjectURL(pdfBlob);
    } catch {
      lastError = "Не удалось загрузить файл";
    }
  }
  throw new Error(lastError);
};

const DocumentPdfDrawer = ({ open, document, loading = false, onClose }) => {
  const classes = useStyles();
  const fileUrl = document?.url ? getUrlForStaticApi(document.url) : "";
  const title = document?.original_name || "Документ задания";
  const isPdf = isPdfDocument(document);

  const [previewUrl, setPreviewUrl] = React.useState("");
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    const load = async () => {
      setPreviewUrl("");
      setPreviewError("");
      if (!open || !document?.url || !isPdf) return;

      setPreviewLoading(true);
      try {
        objectUrl = await fetchPdfBlobUrl(document.url);
        if (!cancelled) setPreviewUrl(objectUrl);
      } catch (error) {
        if (!cancelled) setPreviewError(error?.message || "Не удалось открыть PDF");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, document?.url, document?.original_name, document?.format, isPdf]);

  const handleDownload = () => {
    if (!document?.url) return;
    downloadFile(document.url, title);
  };

  const showLoading = loading || previewLoading;
  const canPreview = Boolean(previewUrl) && !previewError;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.paper }}
      sx={{ "& .MuiDrawer-paper": { right: "10px !important" } }}
    >
      <div className={classes.header}>
        <Typography className={classes.title} title={title}>{title}</Typography>
        <Box className={classes.headerActions}>
          {document?.url ? (
            <IconButton
              size="small"
              className={classes.downloadBtn}
              onClick={handleDownload}
              aria-label="Скачать"
            >
              <CloudDownloadIcon />
            </IconButton>
          ) : null}
          <IconButton onClick={onClose} size="small" aria-label="Закрыть">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </div>

      <div className={classes.body}>
        {showLoading ? (
          <div className={classes.stateBox}>
            <CircularProgress />
            <Typography className={classes.stateText}>Загрузка PDF…</Typography>
          </div>
        ) : !document?.url ? (
          <div className={classes.stateBox}>
            <Typography className={classes.stateText}>Файл документа не найден</Typography>
          </div>
        ) : !isPdf ? (
          <div className={classes.stateBox}>
            <Typography className={classes.stateText}>
              Предпросмотр доступен для PDF. Скачайте файл, чтобы открыть его.
            </Typography>
            <Button variant="contained" className={classes.actionBtn} onClick={handleDownload}>
              Скачать файл
            </Button>
          </div>
        ) : previewError ? (
          <div className={classes.stateBox}>
            <Typography className={classes.stateTitle}>Не удалось показать PDF</Typography>
            <Typography className={classes.stateText}>{previewError}</Typography>
            <Box className={classes.actions}>
              <Button variant="outlined" className={classes.secondaryBtn} onClick={handleDownload}>
                Скачать
              </Button>
              {fileUrl ? (
                <Button
                  variant="contained"
                  className={classes.actionBtn}
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Открыть в новой вкладке
                </Button>
              ) : null}
            </Box>
          </div>
        ) : canPreview ? (
          <iframe
            title={title}
            src={previewUrl}
            className={classes.frame}
          />
        ) : (
          <div className={classes.stateBox}>
            <Typography className={classes.stateText}>Нет данных для просмотра</Typography>
          </div>
        )}
      </div>
    </Drawer>
  );
};

const useStyles = makeStyles(() => ({
  paper: {
    maxWidth: "100%",
    borderLeft: "1px solid #E4E4E7",
    zIndex: "1400 !important",
    boxShadow: "-8px 0 24px -4px rgba(0, 0, 0, 0.08)",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    "@media (min-width: 600px)": {
      width: 860,
      top: 12,
      height: "calc(100% - 24px)",
      borderRadius: 10,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 20px",
    borderBottom: "1px solid #E4E4E7",
    flexShrink: 0,
    backgroundColor: "#FFFFFF",
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "#27272A",
    lineHeight: "24px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  downloadBtn: {
    color: "#71717A",
  },
  body: {
    flex: 1,
    minHeight: 0,
    position: "relative",
    backgroundColor: "#F4F4F5",
  },
  frame: {
    position: "absolute",
    inset: 0,
    border: "none",
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
  },
  stateBox: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: 24,
  },
  stateTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#27272A",
    textAlign: "center",
  },
  stateText: {
    fontSize: 14,
    color: "#71717A",
    textAlign: "center",
    maxWidth: 360,
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: 4,
  },
  actionBtn: {
    textTransform: "none",
    fontWeight: 500,
    backgroundColor: "#4F39F6",
    "&:hover": { backgroundColor: "#4338CA" },
  },
  secondaryBtn: {
    textTransform: "none",
    fontWeight: 500,
  },
}));

export default DocumentPdfDrawer;
